/**
 * P0 — Verkoopfacturen (Sales Invoices) backoffice sectie.
 *
 * Twee tabs:
 *   - Overzicht: lijst met filters (range/status/source/search), acties per rij
 *     (openen, PDF download, e-mail, status wijzigen, dupliceren)
 *   - Nieuwe factuur: directe (manual) factuur aanmaken met NAW + lines + presets
 *
 * Hergebruikt:
 *   - bestaande GET /api/admin/sales-invoices (uitgebreid met filters)
 *   - bestaande GET /api/admin/sales-invoices/{id}/pdf
 *   - bestaande POST /api/admin/sales-invoices/{id}/email
 *
 * Nieuwe endpoints (P0):
 *   - POST /api/admin/sales-invoices (manual)
 *   - PATCH /api/admin/sales-invoices/{id}
 *   - POST /api/admin/sales-invoices/{id}/status
 *   - POST /api/admin/sales-invoices/{id}/duplicate
 *   - GET /api/admin/sales-invoices/meta
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  FileText, Plus, Download, Mail, Copy, Search, X, Loader2,
  Trash2, CheckCircle2, Archive, ArchiveRestore, XCircle, AlertTriangle, Edit3,
  FileMinus2, Eye, Printer,
} from "lucide-react";
import { AdminPageHeader, AdminKpiCard } from "../ui";
import EnterpriseEntityPicker from "../shared/EnterpriseEntityPicker";

import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "../../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../ui/select";

import { adminApi, useAuth } from "../../../context/AuthContext";
import NAWAddressBlock from "./NAWAddressBlock";
import InvoicePaymentsBlock, { PaymentStatusPill } from "./InvoicePaymentsBlock";
import InvoiceEditDialog from "./InvoiceEditDialog";
import CreditNoteDialog from "./CreditNoteDialog";
import { CreditRelationsPanel } from "./CreditRelationsPanel";
import InvoiceCustomerLinkPanel from "./InvoiceCustomerLinkPanel";
import InvoiceLineCards from "./InvoiceLineCards";
// BetalingsopvolgingTab is verhuisd naar Debiteurenbeheer (Feb 2026 IA-refactor)
import FactuurBetalingsopvolgingPanel from "./FactuurBetalingsopvolgingPanel";
import useDownloadPdf from "../../../hooks/useDownloadPdf";
import ContactPersonSelector from "../../shared/ContactPersonSelector";
import DocumentPreviewDialog from "../shared/DocumentPreviewDialog";

const STATUS_LABEL = {
  concept: { label: "Concept", cls: "bg-gray-100 text-gray-700 border-gray-200" },
  open: { label: "Onbetaald", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  verzonden: { label: "Verzonden", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  deels_betaald: { label: "Deels betaald", cls: "bg-amber-100 text-amber-900 border-amber-300" },
  betaald: { label: "Betaald", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  geannuleerd: { label: "Geannuleerd", cls: "bg-red-50 text-red-700 border-red-200" },
  // Sprint 1 Feb 2026 — Creditnota-statussen voor originele facturen.
  gecrediteerd: { label: "Gecrediteerd", cls: "bg-purple-50 text-purple-800 border-purple-200" },
  deels_gecrediteerd: { label: "Deels gecrediteerd", cls: "bg-purple-100 text-purple-900 border-purple-300" },
};

const SOURCE_LABEL = {
  quote: "Offerte",
  manual: "Direct",
};

function fmtEUR(value, currency = "EUR") {
  const num = Number(value || 0);
  return `${currency} ${num.toFixed(2).replace(".", ",")}`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  return iso.slice(0, 10);
}

export default function SalesInvoicesSection({ prefilter = null, onClearPrefilter, initialInvoiceId = null } = {}) {
  const [tab, setTab] = useState("overview"); // Feb 2026 IA-refactor — Facturen = administratie
  const [meta, setMeta] = useState(null);
  const [kpis, setKpis] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await adminApi.get("/admin/sales-invoices/meta");
        setMeta(r.data);
      } catch {/* ignore */}
    })();
    // Feb 2026 IA-refactor — administratief KPI-dashboard bovenaan Facturen
    (async () => {
      try {
        const r = await adminApi.get("/admin/sales-invoices/dashboard-kpis");
        setKpis(r.data);
      } catch {/* ignore */}
    })();
  }, []);

  return (
    <div className="space-y-5" data-testid="sales-invoices-section">
      <AdminPageHeader
        icon={FileText}
        title="Facturen"
        subtitle="Verkoopfacturen — direct of vanuit offerte. Downloaden, mailen en statussen beheren."
        actions={
          <Button
            onClick={() => setTab("new")}
            className="rounded-full bg-brand-900 hover:bg-brand-700 text-white font-chivo font-bold uppercase text-xs shadow-brand-soft"
            data-testid="invoice-new-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Nieuwe factuur
          </Button>
        }
      />

      {prefilter && prefilter.type === "customer" && (
        <div
          className="flex items-center justify-between gap-3 bg-blue-50 border border-blue-200 px-3 py-2 rounded-xl"
          data-testid="invoice-prefilter-banner"
        >
          <div className="text-xs text-blue-900">
            <span className="font-bold uppercase tracking-wide mr-2">Gefilterd op klant:</span>
            <span className="font-medium" data-testid="invoice-prefilter-name">{prefilter.customer_name}</span>
            {prefilter.customer_email && <span className="text-blue-700"> · {prefilter.customer_email}</span>}
          </div>
          <button
            type="button"
            onClick={() => onClearPrefilter?.()}
            className="text-xs text-blue-900 hover:text-blue-700 underline font-medium"
            data-testid="invoice-prefilter-clear"
          >
            Filter wissen
          </button>
        </div>
      )}

      {/* Feb 2026 IA-refactor — administratief KPI-dashboard bovenaan.
          Betalingsopvolging is verhuisd naar Debiteurenbeheer. Deze module
          focust nu op FACTUUR-administratie: nieuw, bewerken, PDF, mailen,
          statuswijziging. */}
      {kpis && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <AdminKpiCard label="Concept" value={kpis.concept ?? 0} icon={FileText} tone="slate" testid="invoice-kpi-concept" />
          <AdminKpiCard label="Verzonden" value={kpis.verzonden ?? 0} icon={FileText} tone="brand" testid="invoice-kpi-verzonden" />
          <AdminKpiCard label="Deels betaald" value={kpis.deels_betaald ?? 0} icon={FileText} tone="amber" testid="invoice-kpi-deels" />
          <AdminKpiCard label="Betaald" value={kpis.betaald ?? 0} icon={FileText} tone="emerald" testid="invoice-kpi-betaald" />
          <AdminKpiCard label="Vervallen" value={kpis.vervallen ?? 0} icon={FileText} tone="red" testid="invoice-kpi-vervallen" />
          <AdminKpiCard
            label="Openstaand totaal"
            value={kpis.openstaand_totaal != null
              ? `€ ${Number(kpis.openstaand_totaal).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : "—"}
            icon={FileText}
            tone="brand"
            testid="invoice-kpi-openstaand-totaal"
          />
        </div>
      )}

      <div className="flex gap-1 border border-gray-200 bg-white w-fit">
        {[
          ["overview", "Facturen"],
          ["new", "Nieuwe factuur"],
        ].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`invoice-tab-${k}`}
            className={`px-4 py-2 text-xs uppercase font-bold tracking-wide ${
              tab === k ? "bg-[#082c50] text-white" : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab meta={meta} prefilter={prefilter} initialInvoiceId={initialInvoiceId} />}
      {tab === "new" && <NewInvoiceTab meta={meta} onCreated={() => setTab("overview")} />}
    </div>
  );
}

// ============================================================
// OVERVIEW
// ============================================================

function OverviewTab({ meta, prefilter, initialInvoiceId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("");
  const [statusF, setStatusF] = useState("");
  const [sourceF, setSourceF] = useState("");
  const [archivedF, setArchivedF] = useState("hidden"); // 'hidden' | 'only' | 'all'
  // Sprint 1 Feb 2026 — Filter op document_type. Default 'invoice' toont
  // alleen verkoopfacturen (excl. creditnota's). 'credit_note' toont
  // alleen creditnota's; 'all' toont beide.
  const [docTypeF, setDocTypeF] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);
  const triggerReload = () => setReloadTick((n) => n + 1);
  const openInvoice = async (id) => {
    try {
      const { data } = await adminApi.get(`/admin/sales-invoices/${id}`);
      setSelected(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Document kon niet worden geopend");
    }
  };
  const customerIdFilter = prefilter?.type === "customer" ? prefilter.customer_id : null;

  // Iteratie A — deep-link: open invoice detail bij mount indien
  // initialInvoiceId aanwezig (via entity-search of directe URL).
  // Query-param wordt BEHOUDEN zodat F5 het detail opnieuw opent.
  useEffect(() => {
    if (!initialInvoiceId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await adminApi.get(`/admin/sales-invoices/${initialInvoiceId}`);
        if (cancelled) return;
        setSelected(r.data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Factuur kon niet worden geopend");
      }
    })();
    return () => { cancelled = true; };
  }, [initialInvoiceId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = { limit: 200 };
        if (range) params.range_ = range;
        if (statusF) params.status = statusF;
        if (sourceF) params.source_type = sourceF;
        if (archivedF && archivedF !== "hidden") params.archived = archivedF;
        if (q.trim()) params.q = q.trim();
        if (customerIdFilter) params.customer_id = customerIdFilter;
        // Sprint 1 Feb 2026 — document_type filter
        if (docTypeF && docTypeF !== "invoice") params.document_type = docTypeF;
        const r = await adminApi.get("/admin/sales-invoices", { params });
        if (!cancelled) setItems(r.data?.items || []);
      } catch (err) {
        if (!cancelled) toast.error(err?.response?.data?.detail || "Laden mislukt");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [range, statusF, sourceF, archivedF, reloadTick, customerIdFilter, docTypeF]);

  return (
    <div className="space-y-3" data-testid="invoice-overview-tab">
      <div className="bg-white border border-gray-200 p-3 flex flex-wrap items-center gap-2">
        <div className="flex gap-1 border border-gray-200">
          {[["", "Alle"], ["today", "Vandaag"], ["this_week", "Deze week"], ["overdue", "Vervallen"]].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setRange(k)}
              data-testid={`invoice-range-${k || "all"}`}
              className={`px-3 py-1.5 text-xs uppercase font-bold ${
                range === k ? "bg-[#082c50] text-white" : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
        <Select value={statusF || "all"} onValueChange={(v) => setStatusF(v === "all" ? "" : v)}>
          <SelectTrigger className="h-9 text-xs rounded-xl w-36" data-testid="invoice-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle statussen</SelectItem>
            {(meta?.statuses || []).map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]?.label || s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sourceF || "all"} onValueChange={(v) => setSourceF(v === "all" ? "" : v)}>
          <SelectTrigger className="h-9 text-xs rounded-xl w-36" data-testid="invoice-filter-source"><SelectValue placeholder="Bron" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle bronnen</SelectItem>
            <SelectItem value="manual">Direct</SelectItem>
            <SelectItem value="quote">Offerte</SelectItem>
          </SelectContent>
        </Select>
        <Select value={docTypeF} onValueChange={setDocTypeF}>
          <SelectTrigger className="h-9 text-xs rounded-xl w-36" data-testid="invoice-filter-doctype"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="invoice" data-testid="invoice-doctype-invoice">Facturen</SelectItem>
            <SelectItem value="credit_note" data-testid="invoice-doctype-credit">Creditnota&apos;s</SelectItem>
            <SelectItem value="all" data-testid="invoice-doctype-all">Beide</SelectItem>
          </SelectContent>
        </Select>
        <Select value={archivedF} onValueChange={setArchivedF}>
          <SelectTrigger className="h-9 text-xs rounded-xl w-40" data-testid="invoice-filter-archived"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="hidden">Actief (geen archief)</SelectItem>
            <SelectItem value="only">Alleen gearchiveerd</SelectItem>
            <SelectItem value="all">Alles incl. archief</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex-1 min-w-[200px] relative">
          <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && triggerReload()}
            placeholder="Zoek factuurnummer, klant, referentie..."
            className="pl-7 rounded-xl text-xs h-9"
            data-testid="invoice-search"
          />
        </div>
      </div>

      <div className="bg-white border border-gray-200 min-w-0">
        {loading ? (
          <div className="p-10 text-center text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Laden...</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-gray-500" data-testid="invoice-overview-empty">
            Geen facturen voor dit filter.
          </div>
        ) : (
          <>
          <div className="md:hidden divide-y" data-testid="invoice-mobile-list">
            {items.map(inv => <article key={inv.id} className="p-3 space-y-2 text-xs break-words" data-testid={`invoice-mobile-row-${inv.id}`}>
              <div className="flex flex-wrap justify-between gap-2"><strong data-testid={`invoice-mobile-number-${inv.id}`}>{inv.invoice_number}</strong><span data-testid={`invoice-mobile-type-${inv.id}`}>{inv.document_type === "credit_note" ? "Creditnota" : "Factuur"}</span></div>
              <p data-testid={`invoice-mobile-customer-${inv.id}`}>{inv.customer_name}</p>
              {inv.original_invoice_number && <p data-testid={`invoice-mobile-original-${inv.id}`}>Voor {inv.original_invoice_number}</p>}
              <div className="flex flex-wrap items-center justify-between gap-2"><span className="font-mono" data-testid={`invoice-mobile-total-${inv.id}`}>{fmtEUR(inv.total_incl_vat, inv.currency)}</span><Button variant="outline" size="sm" onClick={() => setSelected(inv)} data-testid={`invoice-mobile-open-${inv.id}`}>Openen</Button></div>
            </article>)}
          </div>
          <table className="w-full text-sm hidden md:table [overflow-wrap:anywhere]">
            <thead className="bg-gray-50 text-[10px] uppercase text-gray-600">
              <tr>
                <th className="text-left p-2">Nummer</th>
                <th className="text-left p-2">Klant</th>
                <th className="text-left p-2 hidden md:table-cell">Datum</th>
                <th className="text-left p-2 hidden md:table-cell">Vervalt</th>
                <th className="text-right p-2">Totaal</th>
                <th className="text-right p-2 hidden sm:table-cell">Betaald</th>
                <th className="text-right p-2 hidden sm:table-cell">Openstaand</th>
                <th className="text-center p-2">Status</th>
                <th className="text-center p-2 hidden lg:table-cell">Bron</th>
                <th className="text-right p-2">Acties</th>
              </tr>
            </thead>
            <tbody>
              {items.map((inv) => {
                const st = STATUS_LABEL[inv.status] || { label: inv.status, cls: "bg-gray-100 text-gray-700 border-gray-200" };
                const totalPaid = Number(inv.total_paid || 0);
                const totalInvoice = Number(inv.total_incl_vat || 0);
                const amountDue = Number(inv.amount_due ?? Math.max(0, totalInvoice - totalPaid));
                const isCredit = inv.document_type === "credit_note";
                const overpaid = !isCredit && (!!inv.overpaid || totalPaid > totalInvoice);
                return (
                  <tr key={inv.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`invoice-row-${inv.id}`}>
                    <td className="p-2 font-mono text-xs" data-testid={`invoice-number-${inv.id}`}>{inv.invoice_number}{isCredit && <div className="text-gray-500 font-sans" data-testid={`invoice-credit-label-${inv.id}`}>Creditnota · {inv.original_invoice_number}</div>}</td>
                    <td className="p-2">
                      <div className="font-bold">{inv.customer_name}</div>
                      <div className="text-[10px] text-gray-500">{inv.customer_email || "—"}</div>
                      <div className="text-[10px] text-gray-400 md:hidden">{fmtDate(inv.invoice_date)} → {fmtDate(inv.due_date)}</div>
                      <div className="text-[10px] text-gray-500 sm:hidden font-mono">
                        Betaald {fmtEUR(totalPaid, inv.currency)} · Open {fmtEUR(amountDue, inv.currency)}
                      </div>
                    </td>
                    <td className="p-2 text-xs hidden md:table-cell">{fmtDate(inv.invoice_date)}</td>
                    <td className="p-2 text-xs hidden md:table-cell">{fmtDate(inv.due_date)}</td>
                    <td className="p-2 text-right font-mono text-xs">{fmtEUR(totalInvoice, inv.currency)}</td>
                    <td className={`p-2 text-right font-mono text-xs hidden sm:table-cell ${totalPaid > 0 ? "text-emerald-700" : "text-gray-400"}`} data-testid={`invoice-row-paid-${inv.id}`}>
                      {fmtEUR(totalPaid, inv.currency)}
                    </td>
                    <td className={`p-2 text-right font-mono text-xs hidden sm:table-cell ${overpaid ? "text-purple-700" : amountDue > 0 ? "text-amber-800" : "text-emerald-700"}`} data-testid={`invoice-row-due-${inv.id}`}>
                      {overpaid ? `-${fmtEUR(totalPaid - totalInvoice, inv.currency)}` : fmtEUR(amountDue, inv.currency)}
                    </td>
                    <td className="p-2 text-center">
                      <span className={`inline-block text-[10px] uppercase font-bold px-2 py-0.5 border ${st.cls}`}>
                        {isCredit ? (inv.last_emailed_at ? "Verzonden" : "Niet verzonden") : overpaid ? "Overbetaald" : st.label}
                      </span>
                      {inv.archived && (
                        <div className="text-[9px] uppercase font-bold text-slate-600 mt-0.5" data-testid={`invoice-row-archived-${inv.id}`}>
                          Gearchiveerd
                        </div>
                      )}
                      {inv.last_payment_date && (
                        <div className="text-[9px] text-gray-500 mt-0.5">
                          {fmtDate(inv.last_payment_date)} · {inv.last_payment_method || ""}
                        </div>
                      )}
                    </td>
                    <td className="p-2 text-center text-[10px] uppercase font-bold text-gray-500 hidden lg:table-cell">
                      {isCredit ? "Creditnota" : SOURCE_LABEL[inv.source_type] || (inv.quote_id ? "Offerte" : "Direct")}
                    </td>
                    <td className="p-2 text-right">
                      <Button size="sm" variant="ghost" onClick={() => setSelected(inv)} className="h-7 px-2 text-xs" data-testid={`invoice-open-${inv.id}`}>
                        Openen
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </>
        )}
      </div>

      {selected && (
        <InvoiceDetailDialog
          key={selected.id}
          inv={selected}
          onOpenInvoice={openInvoice}
          meta={meta}
          onClose={() => setSelected(null)}
          onChanged={() => { triggerReload(); }}
        />
      )}
    </div>
  );
}

// ============================================================
// DETAIL DIALOG
// ============================================================

function InvoiceDetailDialog({ inv: invInitial, meta, onClose, onChanged, onOpenInvoice }) {
  const { user, hasPermission } = useAuth();
  const userRole = (user?.role || "").toLowerCase();
  const isOwnerOrAdmin = userRole === "owner" || userRole === "admin";
  const isMedewerker = userRole === "medewerker";
  const canManagePayments = !isMedewerker && hasPermission("orders:convert_financial");
  const canManageLink =
    hasPermission("orders:convert_financial") ||
    ["owner", "admin", "manager"].includes(userRole);

  const [inv, setInv] = useState(invInitial);
  const [busy, setBusy] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  // Sprint 1 Feb 2026 — Factuur Crediteren dialog.
  const [creditOpen, setCreditOpen] = useState(false);
  // Jun 2026 — Bekijken/Printen via de veilige canonical previewer.
  const [pdfPreview, setPdfPreview] = useState(null);
  const [paymentTick, setPaymentTick] = useState(0);
  const pdfDl = useDownloadPdf();

  const status = inv.status || "concept";
  const isConcept = status === "concept";
  const isDefinitief = ["open", "verzonden", "deels_betaald"].includes(status);
  const isBetaald = status === "betaald";
  const isGeannuleerd = status === "geannuleerd";
  const isArchived = !!inv.archived;
  const isDeleted = !!inv.deleted_at;
  const isCreditNote = (inv.document_type || "invoice") === "credit_note";
  const isFullyCredited = status === "gecrediteerd";
  const isProtectedEdit = status === "betaald" || status === "deels_betaald";

  const totalPaid = Number(inv.total_paid || 0);
  const totalInvoice = Number(inv.total_incl_vat || 0);
  const overpaid = !isCreditNote && (!!inv.overpaid || totalPaid > totalInvoice);

  const canDelete = !isCreditNote && isConcept && !isDeleted && !isMedewerker;
  const canCancel = !isCreditNote && (isDefinitief || (isBetaald && isOwnerOrAdmin)) && !isMedewerker && !isDeleted;
  const canArchive = !isConcept && !isArchived && !isDeleted && !isMedewerker;
  const canUnarchive = isArchived && !isDeleted && !isMedewerker;
  const canEmail = hasPermission("invoices:send") && !isGeannuleerd && !isDeleted;
  const canEdit = !isCreditNote && !inv.credit_note_ids?.length && !inv.total_credited && !isGeannuleerd && !isDeleted && !isMedewerker && (!isProtectedEdit || canManagePayments);
  const canDuplicate = !isCreditNote && !isDeleted && !isMedewerker;
  // Sprint 1 Feb 2026 — Crediteren mag alleen op verkoopfacturen (niet
  // op creditnota's zelf), niet op concept/geannuleerd/reeds volledig
  // gecrediteerd, en alleen door rollen met invoices:credit.
  const canCredit = (
    hasPermission("invoices:credit")
    && !isCreditNote
    && !isConcept
    && !isGeannuleerd
    && !isFullyCredited
    && !isDeleted
  );

  const refresh = useCallback(async () => {
    try {
      const r = await adminApi.get(`/admin/sales-invoices/${inv.id}`);
      setInv(r.data);
      onChanged();
    } catch {/* ignore */}
  }, [inv.id, onChanged]);

  // P1 4 Mrt 2026 — Fetch full detail bij openen (list-view geeft geen
  // linked_customer + _suggested_matches mee).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await adminApi.get(`/admin/sales-invoices/${invInitial.id}`);
        if (!cancelled) setInv(r.data);
      } catch {/* ignore */}
    })();
    return () => { cancelled = true; };
  }, [invInitial.id]);

  const handleDownloadPDF = async () => {
    await pdfDl.download(
      async () => {
        const r = await adminApi.get(`/admin/sales-invoices/${inv.id}/pdf`, { responseType: "blob" });
        return {
          blob: new Blob([r.data], { type: "application/pdf" }),
          filename: `${inv.invoice_number || "factuur"}.pdf`,
        };
      },
      {
        loadingMessage: "Factuur wordt gegenereerd…",
        successMessage: "Factuur klaar — download gestart.",
        errorMessage: "PDF download mislukt. Probeer het opnieuw.",
        taskId: `invoice-pdf-${inv.id}`,
      },
    );
  };

  // P0 sprint — Statuswijziging.
  // Handmatig "Betaald" of "Deels betaald" zetten is geblokkeerd in backend (409).
  // We vangen die hier af en gidsen de gebruiker naar de Betalingen-sectie.
  const handleStatusChange = async (newStatus) => {
    if (newStatus === inv.status) return;
    if (newStatus === "betaald" || newStatus === "deels_betaald") {
      toast.error(
        "Betaalstatus wordt automatisch bepaald. Registreer in plaats een betaling via 'Betaling registreren' hieronder.",
        { duration: 6000 },
      );
      return;
    }
    setBusy(true);
    try {
      await adminApi.post(`/admin/sales-invoices/${inv.id}/status`, { status: newStatus });
      toast.success("Status bijgewerkt");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Statuswijziging mislukt");
    } finally {
      setBusy(false);
    }
  };

  const handleDuplicate = async () => {
    setBusy(true);
    try {
      const r = await adminApi.post(`/admin/sales-invoices/${inv.id}/duplicate`);
      toast.success(`Dupliceer aangemaakt: ${r.data.invoice_number}`);
      onChanged();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Dupliceren mislukt");
    } finally {
      setBusy(false);
    }
  };

  const handleArchiveToggle = async () => {
    setBusy(true);
    try {
      const endpoint = isArchived ? "unarchive" : "archive";
      await adminApi.post(`/admin/sales-invoices/${inv.id}/${endpoint}`);
      toast.success(isArchived ? "Factuur teruggehaald uit archief" : "Factuur gearchiveerd");
      onChanged();
      // bij archiveren sluit dialog (verdwijnt uit standaardlijst)
      if (!isArchived) {
        onClose();
      } else {
        await refresh();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Actie mislukt");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl w-[calc(100%_-_1.5rem)] grid-cols-[minmax(0,1fr)] [&>*]:min-w-0 [overflow-wrap:anywhere] max-h-[92vh] overflow-y-auto" data-testid="invoice-detail">
        <DialogHeader>
          <DialogTitle className="font-chivo font-black text-xl text-[#082c50] flex items-center gap-2 flex-wrap">
            <span className="break-all" data-testid="invoice-document-title">{isCreditNote ? "Creditnota" : "Factuur"} {inv.invoice_number}</span>
            {isCreditNote ? <span className="text-xs" data-testid="credit-delivery-status">{inv.last_emailed_at ? "Aan mailprovider aangeboden" : "Nog niet per e-mail verzonden"}</span> : <PaymentStatusPill status={inv.status} overpaid={overpaid} />}
            {isArchived && (
              <span className="text-[10px] uppercase font-bold px-2 py-0.5 border bg-slate-100 text-slate-700 border-slate-300" data-testid="invoice-badge-archived">
                Gearchiveerd
              </span>
            )}
            {isDeleted && (
              <span className="text-[10px] uppercase font-bold px-2 py-0.5 border bg-red-100 text-red-700 border-red-300" data-testid="invoice-badge-deleted">
                Verwijderd
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {inv.customer_name} · {fmtDate(inv.invoice_date)}{!isCreditNote && ` · vervalt ${fmtDate(inv.due_date)}`}
            {inv.cancel_reason && (
              <>
                <br />
                <span className="text-red-700">Reden annulering: {inv.cancel_reason}</span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2 pb-3 border-b border-gray-200">
          <Button size="sm" onClick={handleDownloadPDF} disabled={busy} className="bg-[#082c50] text-white rounded-xl text-xs" data-testid="invoice-download-pdf">
            <Download className="w-3 h-3 mr-1" /> PDF
          </Button>
          <Button size="sm" variant="outline" onClick={() => setPdfPreview({ autoPrint: false })} disabled={busy} className="rounded-xl text-xs" data-testid="invoice-view-pdf">
            <Eye className="w-3 h-3 mr-1" /> Bekijken
          </Button>
          <Button size="sm" variant="outline" onClick={() => setPdfPreview({ autoPrint: true })} disabled={busy} className="rounded-xl text-xs" data-testid="invoice-print-pdf">
            <Printer className="w-3 h-3 mr-1" /> Printen
          </Button>
          {canEmail && (
            <Button size="sm" variant="outline" onClick={() => setEmailOpen(true)} disabled={busy} className="rounded-xl text-xs" data-testid="invoice-email-btn">
              <Mail className="w-3 h-3 mr-1" /> {inv.last_emailed_at ? "Opnieuw verzenden" : "E-mail verzenden"}
            </Button>
          )}
          {canEdit && (
            <Button size="sm" variant="outline" onClick={() => setEditOpen(true)} disabled={busy} className="rounded-xl text-xs border-[#082c50] text-[#082c50] hover:bg-blue-50" data-testid="invoice-edit-btn">
              <Edit3 className="w-3 h-3 mr-1" /> Bewerken
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={handleDuplicate} disabled={busy} className={`rounded-xl text-xs ${canDuplicate ? "" : "hidden"}`} data-testid="invoice-duplicate-btn">
            <Copy className="w-3 h-3 mr-1" /> Dupliceren
          </Button>
          {canArchive && (
            <Button size="sm" variant="outline" onClick={handleArchiveToggle} disabled={busy} className="rounded-xl text-xs" data-testid="invoice-archive-btn">
              <Archive className="w-3 h-3 mr-1" /> Archiveren
            </Button>
          )}
          {canUnarchive && (
            <Button size="sm" variant="outline" onClick={handleArchiveToggle} disabled={busy} className="rounded-xl text-xs" data-testid="invoice-unarchive-btn">
              <ArchiveRestore className="w-3 h-3 mr-1" /> Uit archief halen
            </Button>
          )}
          {canCancel && (
            <Button size="sm" variant="outline" onClick={() => setCancelOpen(true)} disabled={busy} className="rounded-xl text-xs border-red-200 text-red-700 hover:bg-red-50" data-testid="invoice-cancel-btn">
              <XCircle className="w-3 h-3 mr-1" /> Annuleren
            </Button>
          )}
          {canCredit && (
            <Button
              size="sm" variant="outline"
              onClick={() => setCreditOpen(true)}
              disabled={busy}
              className="rounded-xl text-xs border-amber-300 text-amber-800 hover:bg-amber-50"
              data-testid="invoice-credit-btn"
            >
              <FileMinus2 className="w-3 h-3 mr-1" /> Crediteren
            </Button>
          )}
          {canDelete && (
            <Button size="sm" variant="outline" onClick={() => setDeleteOpen(true)} disabled={busy} className="rounded-xl text-xs border-red-200 text-red-700 hover:bg-red-50" data-testid="invoice-delete-btn">
              <Trash2 className="w-3 h-3 mr-1" /> Verwijderen
            </Button>
          )}
          {!isCreditNote && !inv.total_credited && !isGeannuleerd && !isDeleted && !isMedewerker && (
            <div className="flex items-center gap-2 ml-auto">
              <Label className="text-xs">Status:</Label>
              <Select value={inv.status} onValueChange={handleStatusChange}>
                <SelectTrigger className="w-44 rounded-xl text-xs h-9" data-testid="invoice-status-change"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(meta?.statuses || []).filter((s) => !["geannuleerd", "gecrediteerd", "deels_gecrediteerd"].includes(s)).map((s) => (
                    <SelectItem key={s} value={s} data-testid={`invoice-status-option-${s}`} disabled={s === "betaald" || s === "deels_betaald"}>
                      {STATUS_LABEL[s]?.label || s}
                      {(s === "betaald" || s === "deels_betaald") && " (auto)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* P1 4 Mrt 2026 — Klantkoppeling (read + koppel/ontkoppel acties) */}
        <InvoiceCustomerLinkPanel
          invoice={inv}
          canManage={!isCreditNote && canManageLink}
          onLinked={refresh}
        />

        {/* Customer + invoice info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div>
            <h4 className="text-[10px] uppercase font-bold text-gray-700 mb-1">Klant</h4>
            <div className="font-bold">{inv.customer_name}</div>
            {inv.customer_company && <div>{inv.customer_company}</div>}
            {inv.customer_email && <div className="text-xs text-gray-600">{inv.customer_email}</div>}
            {inv.customer_phone && <div className="text-xs text-gray-600">{inv.customer_phone}</div>}
            <div className="text-xs text-gray-600 mt-1">{inv.customer_address || "—"}</div>
            {inv.vat_number && <div className="text-xs text-gray-600">BTW: {inv.vat_number}</div>}
          </div>
          <div>
            <h4 className="text-[10px] uppercase font-bold text-gray-700 mb-1">Factuurinfo</h4>
            <div className="text-xs">
              <KV label="Bron" value={isCreditNote ? "Creditnota" : SOURCE_LABEL[inv.source_type] || (inv.quote_id ? "Offerte" : "Direct")} />
              <KV label="Betalingsreferentie" value={inv.payment_reference} />
              {inv.customer_reference && <KV label="Klantreferentie" value={inv.customer_reference} />}
              {inv.service_type && <KV label="Service" value={inv.service_type} />}
              {inv.destination && <KV label="Bestemming" value={inv.destination} />}
              {inv.payment_date && <KV label="Betaaldatum" value={fmtDate(inv.payment_date)} />}
            </div>
          </div>
        </div>

        {/* Lines — SumUp-stijl kaartweergave */}
        <div>
          <h4 className="text-[10px] uppercase font-bold text-gray-700 mb-2 mt-3">Factuurregels</h4>
          <InvoiceLineCards invoice={inv} />
          <div className="mt-3 text-right text-xs space-y-0.5">
            <div>Subtotaal excl. BTW: <span className="font-mono">{fmtEUR(inv.subtotal_excl_vat, inv.currency)}</span></div>
            <div>BTW: <span className="font-mono">{fmtEUR(inv.vat_amount, inv.currency)}</span></div>
            <div className="text-base font-bold text-[#082c50]">Totaal incl. BTW: <span className="font-mono">{fmtEUR(inv.total_incl_vat, inv.currency)}</span></div>
          </div>
        </div>

        {inv.customer_note && (
          <div className="text-xs p-3 bg-gray-50 border border-gray-200">
            <div className="text-[10px] uppercase font-bold text-gray-700 mb-1">Notitie op factuur</div>
            {inv.customer_note}
          </div>
        )}

        {/* Sprint 1 Feb 2026 — Creditnota-relatie panel.
            Toont ofwel: (a) link naar de originele factuur als dit een
            creditnota is, of (b) lijst van gekoppelde creditnota's als
            deze factuur is gecrediteerd. */}
        <CreditRelationsPanel inv={inv} onOpenInvoice={onOpenInvoice} />

        {/* P0 Sprint — Betalingen sectie */}
        {!isCreditNote && !isConcept && !isDeleted && (
          <div className="mt-2 pt-3 border-t border-gray-200">
            <InvoicePaymentsBlock
              key={`payments-${inv.id}-${paymentTick}`}
              invoice={inv}
              onChanged={async () => {
                setPaymentTick((n) => n + 1);
                await refresh();
              }}
            />
          </div>
        )}
        {!isCreditNote && isConcept && (
          <div className="mt-2 pt-3 border-t border-gray-200">
            <div className="text-xs text-gray-600 italic" data-testid="payments-concept-notice">
              Conceptfacturen kunnen nog geen betalingen ontvangen. Wijzig de status naar &apos;Open&apos; of &apos;Verzonden&apos; om
              betalingen te kunnen registreren.
            </div>
          </div>
        )}

        {/* Sprint 5B — Betalingsopvolging paneel */}
        {!isCreditNote && !isConcept && !isDeleted && (
          <div className="mt-2 pt-3 border-t border-gray-200">
            <FactuurBetalingsopvolgingPanel
              invoiceId={inv.id}
              currency={inv.currency || "EUR"}
            />
          </div>
        )}

        {emailOpen && (
          <EmailDialog inv={inv} onClose={() => setEmailOpen(false)} onSent={async () => { setEmailOpen(false); await refresh(); }} />
        )}
        {editOpen && (
          <InvoiceEditDialog
            invoice={inv}
            meta={meta}
            onClose={() => setEditOpen(false)}
            onSaved={async () => { setEditOpen(false); await refresh(); }}
          />
        )}
        {deleteOpen && (
          <ConfirmDeleteDialog
            inv={inv}
            onClose={() => setDeleteOpen(false)}
            onDeleted={() => { setDeleteOpen(false); onChanged(); onClose(); }}
          />
        )}
        {cancelOpen && (
          <ConfirmCancelDialog
            inv={inv}
            onClose={() => setCancelOpen(false)}
            onCancelled={async () => { setCancelOpen(false); await refresh(); }}
          />
        )}
        {creditOpen && (
          <CreditNoteDialog
            open={creditOpen}
            invoice={inv}
            onClose={() => setCreditOpen(false)}
            onCredited={async () => { setCreditOpen(false); await refresh(); onChanged(); }}
          />
        )}
        {pdfPreview && (
          <DocumentPreviewDialog
            open
            onClose={() => setPdfPreview(null)}
            title={`${isCreditNote ? "Creditnota" : "Factuur"} ${inv.invoice_number}`}
            subtitle={[inv.customer_name, fmtEUR(inv.total_incl_vat, inv.currency)]
              .filter(Boolean).join(" · ")}
            url={`/admin/sales-invoices/${inv.id}/pdf`}
            fallbackFilename={`${inv.invoice_number || "factuur"}.pdf`}
            autoPrint={pdfPreview.autoPrint}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ConfirmDeleteDialog({ inv, onClose, onDeleted }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await adminApi.delete(`/admin/sales-invoices/${inv.id}`, {
        data: { reason: reason.trim() || null },
      });
      toast.success(`Conceptfactuur ${inv.invoice_number} verwijderd`);
      onDeleted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verwijderen mislukt");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid="invoice-delete-dialog">
        <DialogHeader>
          <DialogTitle className="font-chivo font-black text-red-700 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" /> Conceptfactuur verwijderen?
          </DialogTitle>
          <DialogDescription>
            Deze conceptfactuur ({inv.invoice_number}) is nog niet verzonden en kan veilig worden verwijderd.
            Deze actie kan niet ongedaan worden gemaakt.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label className="text-[10px] uppercase font-bold">Reden (optioneel)</Label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Bijv. per ongeluk aangemaakt, duplicaat..."
            className="rounded-xl mt-1 text-xs"
            maxLength={500}
            rows={3}
            data-testid="invoice-delete-reason"
          />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-xl text-xs" data-testid="invoice-delete-cancel">
            Annuleren
          </Button>
          <Button onClick={handleConfirm} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs" data-testid="invoice-delete-confirm">
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
            {busy ? "Bezig..." : "Definitief verwijderen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmCancelDialog({ inv, onClose, onCancelled }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await adminApi.post(`/admin/sales-invoices/${inv.id}/cancel`, {
        reason: reason.trim() || null,
      });
      toast.success(`Factuur ${inv.invoice_number} geannuleerd`);
      onCancelled();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Annuleren mislukt");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid="invoice-cancel-dialog">
        <DialogHeader>
          <DialogTitle className="font-chivo font-black text-red-700 flex items-center gap-2">
            <XCircle className="w-5 h-5" /> Factuur annuleren?
          </DialogTitle>
          <DialogDescription>
            Deze factuur ({inv.invoice_number}) heeft een factuurnummer en blijft zichtbaar in de
            administratie. De status wordt gewijzigd naar <strong>Geannuleerd</strong>.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label className="text-[10px] uppercase font-bold">Reden annulering (optioneel)</Label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Bijv. klant heeft order ingetrokken, foutieve factuur, creditfactuur volgt..."
            className="rounded-xl mt-1 text-xs"
            maxLength={500}
            rows={3}
            data-testid="invoice-cancel-reason"
          />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-xl text-xs" data-testid="invoice-cancel-cancel">
            Terug
          </Button>
          <Button onClick={handleConfirm} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs" data-testid="invoice-cancel-confirm">
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <XCircle className="w-3 h-3 mr-1" />}
            {busy ? "Bezig..." : "Ja, factuur annuleren"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmailDialog({ inv, onClose, onSent }) {
  const isCredit = inv.document_type === "credit_note";
  const documentLabel = isCredit ? "Creditnota" : "Factuur";
  const defaultBody = isCredit ? `Beste ${inv.customer_name},\n\nBijgevoegd ontvangt u creditnota ${inv.invoice_number} voor factuur ${inv.original_invoice_number}.\nCreditbedrag: ${fmtEUR(inv.total_incl_vat, inv.currency)}.\n\nEr is geen betaling vereist. Deze creditering is geen bevestiging van een uitgevoerde terugbetaling.\n\nMet vriendelijke groet,\n5999Cargo Group Europe` : `Beste ${inv.customer_name},

Bijgevoegd ontvangt u factuur ${inv.invoice_number} van 5999Cargo Group Europe.

Wij verzoeken u vriendelijk het bedrag van ${fmtEUR(inv.total_incl_vat, inv.currency)} te voldoen onder vermelding van het factuurnummer of betalingskenmerk.

Bankgegevens:
5999Cargo Group Europe
IBAN: IE78 SUMU 9903 6511 0586 77
BIC: SUMUIE22XXX

Heeft u vragen over deze factuur, neem dan contact op via finance@5999cargo.nl.

Met vriendelijke groet,
5999Cargo Group Europe`;
  const [to, setTo] = useState(inv.selected_contact_email || inv.customer_email || "");
  const [subject, setSubject] = useState(`${documentLabel} ${inv.invoice_number} van 5999Cargo Group Europe`);
  const [message, setMessage] = useState(defaultBody);
  const [busy, setBusy] = useState(false);
  // Batch 3 · Fase 5 (scope-close) — Contact-selectie voor factuur-email.
  const [customer, setCustomer] = useState(null);
  const [contactKey, setContactKey] = useState(inv.selected_contact_id || "primary");
  const [selectedContact, setSelectedContact] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // Fetch klant met additional_contacts als er customer_id gekoppeld is
    if (!inv.customer_id) return () => { cancelled = true; };
    (async () => {
      try {
        const r = await adminApi.get(`/admin/customers/${inv.customer_id}/autofill`);
        if (!cancelled) setCustomer(r.data);
      } catch { /* silent — falls back naar customer_email */ }
    })();
    return () => { cancelled = true; };
  }, [inv.customer_id]);

  const handleSend = async () => {
    if (!to.trim()) { toast.error("Ontvanger ontbreekt"); return; }
    setBusy(true);
    try {
      const payload = { to, subject, message, include_pdf: true };
      // Batch 3 · Fase 5 — Snapshot velden meesturen bij expliciete keuze.
      if (inv.customer_id && contactKey && (selectedContact || contactKey === "primary")) {
        payload.selected_contact_id = contactKey === "primary" ? "primary" : contactKey.replace(/^contact-/, "");
        payload.selected_contact_email = (selectedContact?.email) || to;
        payload.selected_contact_name = selectedContact?.name || null;
        payload.selected_contact_phone = selectedContact?.phone || null;
      }
      const { data } = await adminApi.post(`/admin/sales-invoices/${inv.id}/email`, payload);
      if (data.email_status !== "sent") {
        toast.error("Niet verzonden. Controleer de mailvoorziening en het verzendlogboek.");
        return;
      }
      toast.success("E-mail aan de mailprovider aangeboden");
      onSent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verzenden mislukt");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl w-[calc(100%_-_1.5rem)] grid-cols-[minmax(0,1fr)] [&>*]:min-w-0 [overflow-wrap:anywhere] max-h-[90vh] overflow-y-auto" data-testid="invoice-email-dialog">
        <DialogHeader>
          <DialogTitle>{documentLabel} {inv.invoice_number} verzenden</DialogTitle>
          <DialogDescription>PDF wordt automatisch als bijlage meegestuurd.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {/* Batch 3 · Fase 5 — Contact-selector (verschijnt alleen bij
              klanten met extra contactpersonen). customer_id blijft leidend. */}
          <ContactPersonSelector
            customer={customer}
            value={contactKey}
            onSelect={(key, contact) => {
              setContactKey(key);
              setSelectedContact(contact);
              if (contact?.email) {
                setTo(contact.email);
              } else if (contact) {
                setTo("");
                toast.info(`${contact.name} heeft geen e-mailadres. Vul handmatig in of kies andere contactpersoon.`);
              }
            }}
            testIdPrefix="invoice-email-contact"
            hint="Kies bewust een contactpersoon (bv. Boekhouder). Reminders volgen dezelfde persoon."
          />
          <div>
            <Label className="text-[10px] uppercase font-bold">Aan</Label>
            <Input value={to} onChange={(e) => setTo(e.target.value)} className="rounded-xl mt-1" data-testid="invoice-email-to" />
          </div>
          <div>
            <Label className="text-[10px] uppercase font-bold">Onderwerp</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="rounded-xl mt-1" data-testid="invoice-email-subject" />
          </div>
          <div>
            <Label className="text-[10px] uppercase font-bold">Bericht</Label>
            <Textarea rows={10} value={message} onChange={(e) => setMessage(e.target.value)} className="rounded-xl mt-1 font-mono text-xs" data-testid="invoice-email-message" />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-xl text-xs" data-testid="invoice-email-cancel">Annuleren</Button>
          <Button onClick={handleSend} disabled={busy} className="bg-[#082c50] text-white rounded-xl text-xs" data-testid="invoice-email-send">
            {busy ? "Verzenden..." : "Verzenden"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// NEW INVOICE TAB
// ============================================================

function NewInvoiceTab({ meta, onCreated }) {
  const { hasPermission } = useAuth();
  const canSearchCustomers = hasPermission("customers:view");
  const [form, setForm] = useState({
    customer_name: "", customer_company: "", customer_email: "", customer_phone: "",
    customer_whatsapp: "",
    street: "", house_number: "", house_number_addition: "",
    postal_code: "", city: "", country: "Nederland",
    vat_number: "", kvk_number: "",
    invoice_date: new Date().toISOString().slice(0, 10),
    due_days: 14,
    currency: "EUR",
    payment_reference: "", customer_reference: "",
    service_type: "", destination: "",
    customer_note: "", internal_note: "",
  });
  const [lines, setLines] = useState([newEmptyLine()]);
  const [saving, setSaving] = useState(false);
  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  // P1 — Klant selecteren bij factuur aanmaken.
  // Sprint A Finalisatie (22 Feb 2026) — de losse customerQuery/results/
  // searching/dropdownOpen state is verwijderd; de EnterpriseEntityPicker
  // beheert die zelf. Alleen `selectedCustomer` en `applyCustomerToForm`
  // blijven — die zijn nodig voor de "gekoppelde klant"-preview.
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const applyCustomerToForm = (c) => {
    setSelectedCustomer(c);
    // Vul ALLEEN lege velden niet — overschrijf alles met klant-data zodat
    // medewerker een nieuwe selectie krijgt op een schone weergave.
    setForm((s) => ({
      ...s,
      customer_name: [c.first_name, c.last_name].filter(Boolean).join(" ") || c.company_name || "",
      customer_company: c.company_name || "",
      customer_email: (c.email || "").toLowerCase(),
      customer_phone: c.phone || "",
      customer_whatsapp: c.whatsapp || "",
      street: c.address || "",
      house_number: c.house_number || "",
      house_number_addition: c.house_number_addition || "",
      postal_code: c.postal_code || "",
      city: c.city || "",
      country: c.country || "Nederland",
      vat_number: c.btw_number || "",
      kvk_number: c.kvk_number || "",
    }));
  };

  const clearCustomerSelection = () => {
    // Bewaar de huidige formulier-data — alleen de klant-koppeling wordt
    // losgekoppeld, gegevens blijven editable voor handmatige aanpassing.
    setSelectedCustomer(null);
  };

  // Live total preview
  const totals = useMemo(() => {
    let sub = 0, vat = 0;
    lines.forEach((l) => {
      const qty = Number(l.quantity) || 0;
      const unit = Number(l.unit_price) || 0;
      const pct = Number(l.vat_percent) || 0;
      const excl = qty * unit;
      sub += excl;
      vat += excl * pct / 100;
    });
    return { sub, vat, total: sub + vat };
  }, [lines]);

  const addLine = () => setLines((s) => [...s, newEmptyLine()]);
  const removeLine = (i) => setLines((s) => s.length === 1 ? s : s.filter((_, idx) => idx !== i));
  const updateLine = (i, patch) => setLines((s) => s.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const applyPreset = (i, preset) => updateLine(i, {
    description: preset.description, unit: preset.unit, unit_price: preset.unit_price, vat_percent: preset.vat_percent,
  });

  const handleSubmit = async () => {
    if (!form.customer_name && !form.customer_company) {
      toast.error("Klantnaam of bedrijfsnaam is verplicht");
      return;
    }
    if (lines.length === 0 || lines.every((l) => !l.description.trim())) {
      toast.error("Voeg minimaal 1 factuurregel toe");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        customer_id: selectedCustomer?.id || undefined,
        lines: lines.filter((l) => l.description.trim()).map((l) => {
          // P1 Bugfix (Feb 2026): quantity=0 is een geldige gebruikerswaarde
          // en mag niet automatisch naar 1 worden verhoogd. Alleen NaN → 0.
          const qtyNum = Number(l.quantity);
          const priceNum = Number(l.unit_price);
          return {
            description: l.description.trim(),
            quantity: Number.isFinite(qtyNum) ? qtyNum : 0,
            unit: l.unit,
            unit_price: Number.isFinite(priceNum) ? priceNum : 0,
            vat_percent: Number(l.vat_percent),
          };
        }),
      };
      const r = await adminApi.post("/admin/sales-invoices", payload);
      toast.success(`Factuur ${r.data.invoice_number} aangemaakt`);
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Aanmaken mislukt");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="invoice-new-tab">
      {/* P1 — Klant selecteren: optioneel; medewerker mag ook direct
          handmatig de velden invullen. Verbergt zich automatisch wanneer
          de medewerker geen customers:view permissie heeft. */}
      {canSearchCustomers && (
        <div className="bg-white border border-gray-200 p-3 sm:p-4" data-testid="invoice-customer-search-block">
          <h3 className="text-xs uppercase font-bold tracking-wide text-gray-700 mb-3">Klant selecteren</h3>
          {selectedCustomer ? (
            <div className="border border-blue-200 bg-blue-50 p-3 rounded-md" data-testid="invoice-customer-selected-card">
              <div className="flex items-start justify-between gap-2">
                <div className="text-sm text-blue-900 space-y-0.5">
                  <div className="font-bold" data-testid="invoice-customer-selected-name">
                    {selectedCustomer.display_name
                      || [selectedCustomer.first_name, selectedCustomer.last_name].filter(Boolean).join(" ")
                      || selectedCustomer.company_name
                      || selectedCustomer.email
                      || "Onbekende klant"}
                    {selectedCustomer.company_name && [selectedCustomer.first_name, selectedCustomer.last_name].some(Boolean) && (
                      <span className="font-normal text-blue-700"> · {selectedCustomer.company_name}</span>
                    )}
                  </div>
                  <div className="text-xs text-blue-800 space-y-0.5">
                    {selectedCustomer.email && <div>{selectedCustomer.email}</div>}
                    {selectedCustomer.phone && <div>{selectedCustomer.phone}</div>}
                    {(selectedCustomer.address || selectedCustomer.postal_code) && (
                      <div>
                        {[selectedCustomer.address, selectedCustomer.house_number, selectedCustomer.house_number_addition].filter(Boolean).join(" ")}
                        {(selectedCustomer.postal_code || selectedCustomer.city) && (
                          <> · {selectedCustomer.postal_code} {selectedCustomer.city}</>
                        )}
                      </div>
                    )}
                    {selectedCustomer.customer_type && (
                      <div className="uppercase text-[10px] tracking-wide font-bold">{selectedCustomer.customer_type}</div>
                    )}
                    {(selectedCustomer.btw_number || selectedCustomer.kvk_number) && (
                      <div className="font-mono text-[10px]">
                        {selectedCustomer.btw_number && <span>BTW: {selectedCustomer.btw_number}</span>}
                        {selectedCustomer.btw_number && selectedCustomer.kvk_number && <span> · </span>}
                        {selectedCustomer.kvk_number && <span>KVK: {selectedCustomer.kvk_number}</span>}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={clearCustomerSelection}
                  className="text-xs text-blue-900 hover:text-blue-700 underline font-medium whitespace-nowrap"
                  data-testid="invoice-customer-clear-btn"
                >
                  Selectie wissen
                </button>
              </div>
            </div>
          ) : (
            <div>
              {/* Sprint A Finalisatie (22 Feb 2026) — vervangen door
                  EnterpriseEntityPicker. Behoud dezelfde `applyCustomerToForm`
                  flow — SearchResult.metadata bevat alle klant-velden. */}
              <EnterpriseEntityPicker
                entityType="customer"
                mode="inline"
                testIdPrefix="invoice-customer"
                autoFocus={false}
                showRecent={false}
                onSelect={(item) => {
                  applyCustomerToForm({ id: item.id, ...(item.metadata || {}) });
                }}
              />
              <div className="text-[10px] text-gray-500 mt-2">
                Typ minstens 2 tekens om te zoeken. Of vul hieronder handmatig in.
              </div>
            </div>
          )}
        </div>
      )}

      {/* Customer */}
      <div className="bg-white border border-gray-200 p-3 sm:p-4">
        <h3 className="text-xs uppercase font-bold tracking-wide text-gray-700 mb-3">Klantgegevens</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase font-bold">Klantnaam *</Label>
            <Input value={form.customer_name} onChange={(e) => setF("customer_name", e.target.value)} className="rounded-xl mt-1" data-testid="invoice-form-customer_name" />
          </div>
          <div>
            <Label className="text-[10px] uppercase font-bold">Bedrijfsnaam</Label>
            <Input value={form.customer_company} onChange={(e) => setF("customer_company", e.target.value)} className="rounded-xl mt-1" data-testid="invoice-form-company" />
          </div>
          <div>
            <Label className="text-[10px] uppercase font-bold">E-mail</Label>
            <Input type="email" value={form.customer_email} onChange={(e) => setF("customer_email", e.target.value)} className="rounded-xl mt-1" data-testid="invoice-form-email" />
          </div>
          <div>
            <Label className="text-[10px] uppercase font-bold">Telefoon</Label>
            <Input value={form.customer_phone} onChange={(e) => setF("customer_phone", e.target.value)} className="rounded-xl mt-1" data-testid="invoice-form-phone" />
          </div>
          <div className="col-span-1 sm:col-span-2">
            {/* P1 — NAW-blok met PDOK-postcode/huisnummer lookup. Velden gaan
                rechtstreeks naar de bestaande backend SalesInvoice payload. */}
            <NAWAddressBlock
              value={{
                street: form.street,
                house_number: form.house_number,
                house_number_addition: form.house_number_addition,
                postal_code: form.postal_code,
                city: form.city,
                country: form.country,
              }}
              onChange={(patch) => setForm((s) => ({ ...s, ...patch }))}
              testidPrefix="invoice-form-naw"
              title="Klantadres (postcode + huisnummer voor PDOK-lookup)"
              hint="Vul NL postcode + huisnummer in voor automatische straat/plaats."
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase font-bold">BTW-nummer</Label>
            <Input value={form.vat_number} onChange={(e) => setF("vat_number", e.target.value)} className="rounded-xl mt-1" data-testid="invoice-form-vat" />
          </div>
          <div>
            <Label className="text-[10px] uppercase font-bold">KvK</Label>
            <Input value={form.kvk_number} onChange={(e) => setF("kvk_number", e.target.value)} className="rounded-xl mt-1" data-testid="invoice-form-kvk" />
          </div>
        </div>
      </div>

      {/* Invoice info */}
      <div className="bg-white border border-gray-200 p-3 sm:p-4">
        <h3 className="text-xs uppercase font-bold tracking-wide text-gray-700 mb-3">Factuurgegevens</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <Label className="text-[10px] uppercase font-bold">Factuurdatum</Label>
            <Input type="date" value={form.invoice_date} onChange={(e) => setF("invoice_date", e.target.value)} className="rounded-xl mt-1" data-testid="invoice-form-date" />
          </div>
          <div>
            <Label className="text-[10px] uppercase font-bold">Betalingstermijn (dagen)</Label>
            <Input type="number" min={0} value={form.due_days} onChange={(e) => setF("due_days", e.target.value)} className="rounded-xl mt-1" data-testid="invoice-form-due_days" />
          </div>
          <div>
            <Label className="text-[10px] uppercase font-bold">Valuta</Label>
            <Select value={form.currency} onValueChange={(v) => setF("currency", v)}>
              <SelectTrigger className="rounded-xl mt-1" data-testid="invoice-form-currency"><SelectValue /></SelectTrigger>
              <SelectContent>{(meta?.currencies || ["EUR"]).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase font-bold">Klantreferentie</Label>
            <Input value={form.customer_reference} onChange={(e) => setF("customer_reference", e.target.value)} placeholder="bv. warehouse-nr / tracking" className="rounded-xl mt-1" data-testid="invoice-form-customer_reference" />
          </div>
          <div className="col-span-1 sm:col-span-2">
            <Label className="text-[10px] uppercase font-bold">Service / type</Label>
            <Input value={form.service_type} onChange={(e) => setF("service_type", e.target.value)} placeholder="Zeevracht / Luchtvracht / ..." className="rounded-xl mt-1" data-testid="invoice-form-service_type" />
          </div>
          <div className="col-span-1 sm:col-span-2">
            <Label className="text-[10px] uppercase font-bold">Bestemming</Label>
            <Input value={form.destination} onChange={(e) => setF("destination", e.target.value)} placeholder="bv. Curaçao" className="rounded-xl mt-1" data-testid="invoice-form-destination" />
          </div>
          <div className="col-span-1 sm:col-span-2 lg:col-span-4">
            <Label className="text-[10px] uppercase font-bold">Notitie op factuur</Label>
            <Textarea rows={2} value={form.customer_note} onChange={(e) => setF("customer_note", e.target.value)} placeholder="Komt zichtbaar op factuur" className="rounded-xl mt-1" data-testid="invoice-form-customer_note" />
          </div>
          <div className="col-span-1 sm:col-span-2 lg:col-span-4">
            <Label className="text-[10px] uppercase font-bold">Interne notitie</Label>
            <Textarea rows={2} value={form.internal_note} onChange={(e) => setF("internal_note", e.target.value)} placeholder="Alleen voor medewerkers" className="rounded-xl mt-1" data-testid="invoice-form-internal_note" />
          </div>
        </div>
      </div>

      {/* Lines */}
      <div className="bg-white border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs uppercase font-bold tracking-wide text-gray-700">Factuurregels</h3>
          <Button size="sm" onClick={addLine} variant="outline" className="rounded-xl text-xs" data-testid="invoice-add-line">
            <Plus className="w-3 h-3 mr-1" /> Regel toevoegen
          </Button>
        </div>
        <div className="space-y-2">
          {lines.map((l, i) => (
            <LineEditor key={i} idx={i} line={l} meta={meta}
              onUpdate={(patch) => updateLine(i, patch)}
              onRemove={lines.length > 1 ? () => removeLine(i) : null}
              onApplyPreset={(p) => applyPreset(i, p)}
            />
          ))}
        </div>

        {/* Totals preview */}
        <div className="mt-4 p-3 bg-gray-50 border border-gray-200 text-right space-y-1">
          <div className="text-xs">Subtotaal excl. BTW: <span className="font-mono ml-2">{fmtEUR(totals.sub, form.currency)}</span></div>
          <div className="text-xs">BTW: <span className="font-mono ml-2">{fmtEUR(totals.vat, form.currency)}</span></div>
          <div className="text-base font-bold text-[#082c50]" data-testid="invoice-form-total">
            Totaal incl. BTW: <span className="font-mono ml-2">{fmtEUR(totals.total, form.currency)}</span>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 sticky bottom-0 bg-white py-3 border-t border-gray-200">
        <Button onClick={handleSubmit} disabled={saving} className="bg-[#082c50] text-white rounded-xl text-sm font-chivo font-bold uppercase" data-testid="invoice-submit">
          {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Aanmaken...</> : <><CheckCircle2 className="w-4 h-4 mr-1" /> Factuur aanmaken</>}
        </Button>
      </div>
    </div>
  );
}

function newEmptyLine() {
  return { description: "", quantity: 1, unit: "stuk", unit_price: 0, vat_percent: 21 };
}

function LineEditor({ idx, line, meta, onUpdate, onRemove, onApplyPreset }) {
  const lineTotal = (Number(line.quantity) || 0) * (Number(line.unit_price) || 0);
  return (
    <div className="border border-gray-200 p-2 rounded" data-testid={`invoice-line-${idx}`}>
      {/* Mobile: stack alle velden in 2-column grid; desktop: flex-wrap horizontaal */}
      <div className="grid grid-cols-2 sm:flex sm:flex-wrap sm:items-end gap-2">
        <div className="col-span-2 sm:flex-1 sm:min-w-[280px]">
          <Label className="text-[10px] uppercase font-bold">Omschrijving</Label>
          <Input value={line.description} onChange={(e) => onUpdate({ description: e.target.value })} className="rounded-xl mt-1 h-9 text-sm" data-testid={`invoice-line-${idx}-description`} />
        </div>
        <div className="sm:w-20">
          <Label className="text-[10px] uppercase font-bold">Aantal</Label>
          <Input type="number" step="0.01" value={line.quantity} onChange={(e) => onUpdate({ quantity: e.target.value })} className="rounded-xl mt-1 h-9 text-xs" data-testid={`invoice-line-${idx}-quantity`} />
        </div>
        <div className="sm:w-28">
          <Label className="text-[10px] uppercase font-bold">Eenheid</Label>
          <Select value={line.unit || "stuk"} onValueChange={(v) => onUpdate({ unit: v })}>
            <SelectTrigger className="rounded-xl mt-1 h-9 text-xs" data-testid={`invoice-line-${idx}-unit`}><SelectValue /></SelectTrigger>
            <SelectContent>{(meta?.units || []).map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="sm:w-28">
          <Label className="text-[10px] uppercase font-bold">Prijs / eenheid</Label>
          <Input type="number" step="0.01" value={line.unit_price} onChange={(e) => onUpdate({ unit_price: e.target.value })} className="rounded-xl mt-1 h-9 text-xs text-right font-mono" data-testid={`invoice-line-${idx}-unit_price`} />
        </div>
        <div className="sm:w-20">
          <Label className="text-[10px] uppercase font-bold">BTW %</Label>
          <Select value={String(line.vat_percent)} onValueChange={(v) => onUpdate({ vat_percent: Number(v) })}>
            <SelectTrigger className="rounded-xl mt-1 h-9 text-xs" data-testid={`invoice-line-${idx}-vat`}><SelectValue /></SelectTrigger>
            <SelectContent>{(meta?.vat_options || [0, 9, 21]).map((v) => <SelectItem key={v} value={String(v)}>{v}%</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="col-span-2 sm:w-24 flex items-end justify-between sm:justify-end sm:block pb-2">
          <span className="sm:hidden text-[10px] uppercase font-bold text-gray-500">Regeltotaal</span>
          <div className="text-sm sm:text-xs font-mono font-bold text-[#082c50]" data-testid={`invoice-line-${idx}-total`}>
            {lineTotal.toFixed(2).replace(".", ",")}
          </div>
        </div>
        {onRemove && (
          <Button size="sm" variant="ghost" onClick={onRemove} className="h-9 px-2 text-red-600 sm:pb-2" data-testid={`invoice-line-${idx}-remove`}>
            <Trash2 className="w-3 h-3" />
          </Button>
        )}
      </div>
      {/* Presets dropdown */}
      <div className="mt-2 flex items-center gap-2">
        <Label className="text-[10px] uppercase text-gray-500">Preset:</Label>
        <Select value="" onValueChange={(v) => {
          const p = (meta?.line_presets || []).find((x) => x.label === v);
          if (p) onApplyPreset(p);
        }}>
          <SelectTrigger className="rounded-xl h-7 text-[10px] flex-1 sm:w-64 sm:flex-none" data-testid={`invoice-line-${idx}-preset`}>
            <SelectValue placeholder="Kies snelle preset..." />
          </SelectTrigger>
          <SelectContent>
            {(meta?.line_presets || []).map((p) => (
              <SelectItem key={p.label} value={p.label}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div className="flex gap-2">
      <span className="text-[10px] uppercase font-bold text-gray-500 min-w-[110px]">{label}:</span>
      <span>{value || "—"}</span>
    </div>
  );
}



